import { P2GBox } from '@parcel2go/core-ui';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SmartSendItemsCache } from '../../../lib/cache/SmartSendItemsCache';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { CreateFilterAction, SetListyItemsAction } from '../../../lib/context/SmartSendContextModels';
import { SumNumber } from '../../../lib/helpers/ArrayHelper';
import Loading from '../../../../../components/loading/Loading';
import SmartSendSoldItemsActionBar from '../actions/SmartSendSoldItemsActionBar';
import SmartSendGroup from '../item/SmartSendGroup';
import ListyFilterBar from './ListyFilterBar';
import ListyPaging from './ListyPaging';
import SmartSendListyHeaderRowXs from '../SmartSendListyHeaderRowXs';
import {
  ExternalSearch,
  ExternalSearchAwaitingDispatch,
  ExternalSearchInTransit
} from '../../../lib/consts/ListyConsts';
import getExternalSearchQueryParams from '../client/GetExternalSearchQueryParams';
import { P2GAlert } from '@parcel2go/core-ui';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import SmartSendListyHeaderRow from '../SmartSendListyHeaderRow';
import useExternalSearch from '../client/useExternalSearch';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { KeyedSmartSendGroupCache } from '~/apps/smartsend/lib/cache/SmartSendGroupCache';
import { DefaultSubTypeFilter } from '~/apps/smartsend/lib/helpers/DefaultSubTypeFilter';
import ListyNoItems from './ListyNoItems';
import { warnIfStalled } from '~/lib/api/search-diagnostics';
import { AllOrdersPeriod } from '../../dash/DashDateSelection';

export interface SmartSendItemProps {
  initialTab: string;
  isXs: boolean;
  hideHeader?: boolean;
  hideFilter?: boolean;
  take?: number;
  goTo: (route: string) => void;
}

function ListyInner(props: SmartSendItemProps) {
  const { searchExternalSearchAndPollyFillItems } = useExternalSearch();
  const ctx = useContext(SmartSendContext);
  const [loading, setLoading] = useState(false);
  const { goTo } = useGoto();
  const allowAllOrders = props.initialTab === ExternalSearch;

  const [selected, setSelected] = useState({
    take: props.take,
    skip: 0,
    period: allowAllOrders ? AllOrdersPeriod : 'DEFAULT',
    query: '',
    tab: props.initialTab,
    totalPages: 0,
    totalItems: 0
  });

  // Bumped only when the search is cleared from outside the input, so DebouncedInput - which seeds
  // its own state once - remounts and empties. Typing must not change it, or focus would be lost.
  const [searchResetKey, setSearchResetKey] = useState(0);
  const latestRequest = useRef(0);

  const summary = ctx.state.filters[selected.tab].summary;
  const groups = useMemo(
    () => KeyedSmartSendGroupCache[selected.tab] ?? [],
    [KeyedSmartSendGroupCache[selected.tab], selected.tab]
  );

  useEffect(() => {
    const requestId = ++latestRequest.current;

    setLoading(true);
    switch (selected.tab) {
      case ExternalSearch:
      case ExternalSearchInTransit:
      case ExternalSearchAwaitingDispatch: {
        const filters = getExternalSearchQueryParams(selected.period, selected.tab);
        warnIfStalled(
          searchExternalSearchAndPollyFillItems(selected.skip / selected.take, selected.take, filters, selected.query),
          'search'
        )
          .then((r) => {
            // Each search is two round trips (Algolia, then the item enrichment), so replies can
            // land out of order once a debounced search is firing them back to back. Only the
            // newest request is allowed to write.
            if (requestId !== latestRequest.current) {
              return;
            }
            // Items on Main SmartSendContext
            ctx.dispatch(new SetListyItemsAction(r.items, selected.tab, selected.take, selected.skip));
            // New Page State
            setSelected((prev) => ({
              ...prev,
              totalItems: r.totalItems,
              totalPages: r.totalPages
            }));
          })
          .finally(() => {
            if (requestId === latestRequest.current) {
              setLoading(false);
            }
          });
        break;
      }

      default:
        setLoading(false);
    }
  }, [selected.tab, selected.period, selected.take, selected.skip, selected.query]);

  const nextPage = () =>
    setSelected((prev) => ({
      ...prev,
      skip: prev.skip + prev.take
    }));

  const previousPage = () =>
    setSelected((prev) => ({
      ...prev,
      skip: Math.max(0, prev.skip - prev.take)
    }));

  const goToPage = (page: number) =>
    setSelected((prev) => ({
      ...prev,
      skip: (page - 1) * prev.take
    }));

  const setPeriod = (period: string) =>
    setSelected((prev) => ({
      ...prev,
      period: period,
      skip: 0
    }));

  const setQuery = (query: string) =>
    setSelected((prev) => (prev.query === query ? prev : { ...prev, query: query, skip: 0 }));

  const clearQuery = () => {
    setQuery('');
    setSearchResetKey((prev) => prev + 1);
  };

  const setPageSize = (take: number) =>
    setSelected((prev) => ({
      ...prev,
      take: take,
      skip: 0
    }));

  if (!selected.tab || !selected) {
    return <Loading />;
  }

  return (
    <P2GBox>
      <P2GBox pb={2}>
        {!props.hideFilter && (
          <>
            <ListyFilterBar
              period={selected.period}
              selected={selected.tab}
              setPeriod={setPeriod}
              totalItems={selected.totalItems}
              isXs={props.isXs}
              includeAllOrders={allowAllOrders}
              query={selected.query}
              setQuery={setQuery}
              searchResetKey={searchResetKey}
            >
              {[ExternalSearchAwaitingDispatch].includes(selected.tab) &&
                SettingsCache.settings.Features.includes('PackageContents') && (
                  <P2GText>
                    Speed up your labelling by using our{' '}
                    <P2GLink onClick={() => goTo(RoutingConstants.PackageContents)}>picking and packing</P2GLink> tools.
                  </P2GText>
                )}

              {props.isXs && (
                <SmartSendListyHeaderRowXs
                  filterKey={selected.tab}
                  selected={summary.selected.length}
                  total={summary.count}
                />
              )}
            </ListyFilterBar>
            {[ExternalSearchAwaitingDispatch].includes(selected.tab) && (
              <P2GBox mt={2}>
                <P2GAlert type="info">
                  <P2GText variant="body2">
                    After creating your labels, click '<strong>Notify Marketplace</strong>' to send your parcel tracking
                    numbers to your marketplace.
                  </P2GText>
                </P2GAlert>
              </P2GBox>
            )}
          </>
        )}
        <div style={{ minHeight: '500px' }}>
          {groups.length == 0 ? (
            <P2GBox p={2}>
              {loading ? (
                <Loading />
              ) : (
                <ListyNoItems
                  filterKey={selected.tab}
                  goTo={props.goTo}
                  searchTerm={selected.query}
                  onClearSearch={clearQuery}
                />
              )}
            </P2GBox>
          ) : (
            <P2GBox>
              <P2GBox mt={2}>
                {!props.hideHeader && !props.isXs && (
                  <P2GBox mb={2}>
                    <SmartSendListyHeaderRow
                      filterKey={selected.tab}
                      selected={summary.selected.length}
                      total={summary.count}
                    />
                  </P2GBox>
                )}
                {groups.map((x) => (
                  <SmartSendGroup key={`${x.Title}`} group={x} isXs={props.isXs} showTitle={!props.isXs} />
                ))}
              </P2GBox>
            </P2GBox>
          )}
        </div>
      </P2GBox>
      <ListyPaging
        loading={loading}
        skip={selected.skip}
        take={selected.take}
        period={selected.period}
        goToPage={goToPage}
        onBack={previousPage}
        onNext={nextPage}
        totalPages={selected.totalPages}
        itemCount={SumNumber(groups.map((x) => x.Items.length))}
        onPageSizeChanhe={setPageSize}
        isXs={props.isXs}
      />
      <SmartSendSoldItemsActionBar
        filterKey={selected.tab}
        isXs={props.isXs}
        smartSendItemsCache={SmartSendItemsCache.filter((x) => x.SubType === selected.tab)}
      />
    </P2GBox>
  );
}

export default function Listy(props: SmartSendItemProps) {
  const ctx = useContext(SmartSendContext);
  const [loading, setLoading] = useState(true);
  const take = props.take ?? 5;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      if (!ctx.state.filters[props.initialTab]) {
        ctx.dispatch(new CreateFilterAction(props.initialTab, DefaultSubTypeFilter(props.initialTab, take, 0)));
      }
      setLoading(false);
    };
    load();
  }, [props.initialTab]);

  if (loading) {
    return <Loading />;
  }

  return <ListyInner {...props} take={take} />;
}
